import { useForm } from '@abyss/web/hooks/useForm';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { useToast } from '@abyss/web/hooks/useToast';
import { config } from '@abyss/web/tools/config';
import { Flex } from '@abyss/web/ui/Flex';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { PageBody } from '@abyss/web/ui/PageBody';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { adobeSearchFilterEvent } from '../../common/AdobeTagging/adobeSearchFilterEvent';
import { adobeSearchResultClickEvent } from '../../common/AdobeTagging/adobeSearchResultClickEvent';
import {
  BrowseAZ,
  DisableableChipObject,
} from '../../common/BrowseAZ/BrowseAZ';
import { Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useFpcGuestToken } from '../../hooks/useFpcGuestToken';
import { useGeoLocationStorage } from '../../hooks/useGeoLocationStorage';
import { useLagoon } from '../../hooks/useLagoon';
import { removeAccents } from '../../utils/string.utils';
import { useConfig } from '../ProviderSearch/context/Analytics';
import {
  IsExternallink,
  getGuestResultsPageName,
  getLobForPortalData,
  handleGuestPlanNavigation,
} from './Guest.utils';
import {
  ContainerForMultiplePlans,
  ContainerForPlanSelection,
  ContainerPlanNamesFlex,
  PlanInputContainer,
  PlanNameLink,
  SearchInputContainer,
  SelectInputButton,
  StyledCategoryContainer,
  StyledContainerforPage,
  StyledFundedHealthButton,
  StyledListOfCategoryContainer,
  StyledPlanSearch,
  StyledText,
  StyledTextForNoResults,
  planLabel,
} from './GuestMultiplePlan.style';
import { GuestLocation } from './selectLocation/GuestLocation.utils';
import { isDualYearCheckEnabledForGuest } from './utils';

const FPC_BASE_URL = config('FPC_BASE_URL');

export const GuestMultiplePlan = ({
  selectedLocation,
}: {
  selectedLocation: GuestLocation;
}) => {
  const { stateCode, zipcode, longitude, latitude } = useGeoLocationStorage();
  const [searchText, setSearchText] = useState('');
  const featureFlags = useLagoon('feature-flags')();
  const mapKey = useConfig('MAPBOX_KEY');
  const storedPlans = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS
  );

  const { state } = useLocation();
  const plansFromState = state?.availablePlans || [];
  const [, getFpcGuestToken] = useFpcGuestToken({});
  const { toast } = useToast();

  useEffect(() => {
    if (plansFromState.length > 0) {
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS,
        JSON.stringify(plansFromState)
      );
    }
  }, [plansFromState]);

  const availablePlanNames = storedPlans
    ? JSON.parse(storedPlans)
    : plansFromState;

  const { t } = useTranslation();

  const { navigate, getSearchParams } = useRouter();

  const planSelectionConfig = useLagoon('config')();
  const [searchParams] = getSearchParams();
  const lob = searchParams?.get('planSelectionLob') || '';

  const signInUrl = planSelectionConfig?.find(
    (row) => row.key === `SIGNIN_URL_${lob.toUpperCase()}`
  )?.value;

  const lagoonUIMessage = useLagoon('ui-messaging')();

  const noPlansInSelectedArea = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.NO_PLANS_IN_SELECTED_AREA,
  })?.message;
  const enterPlanName = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.ENTER_A_PLAN_NAME,
  })?.message;
  const planName = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.PLAN_NAME,
  })?.message;
  const clear = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CLEAR,
  })?.message;
  const levelFundHealthPlans = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LEVEL_FUNDED_HEALTH_PLANS,
  })?.message;
  const plansAZ = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.PLANS_A_Z,
  })?.message;
  const plansZA = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.PLANS_Z_A,
  })?.message;
  const noMatchingPlans = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.NO_MATCHING_PLANS,
  })?.message;

  const toastMessageForFpcTokenError = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.FPC_ERROR_POPUP_MESSAGE,
  })?.message;

  // Rename the last element in Constants.BROWSE_AZ
  const lastIndex = Constants.BROWSE_AZ.length - 1;
  if (lastIndex >= 0) {
    Constants.BROWSE_AZ[lastIndex].label = clear;
    Constants.BROWSE_AZ[lastIndex].value = 'Clear';
  }

  const letterMapping: any = {};
  const [selectedChip, setSelectedChip] = useState<string>('');
  Constants.BROWSE_AZ.forEach((letter) => {
    letterMapping[letter.label] = [];
  });

  availablePlanNames?.forEach((record) => {
    if (record?.planCategory) {
      // Use planCategory if available
      const startingLetter = removeAccents(record.planCategory.substring(0, 1));
      letterMapping[startingLetter]?.push(record);
    }

    if (record?.planDetails) {
      const planDetails = Array.isArray(record.planDetails)
        ? record.planDetails
        : [record.planDetails];

      planDetails.forEach((plan: any) => {
        const startingLetter = removeAccents(plan?.planName?.substring(0, 1));
        letterMapping[startingLetter]?.push({
          planCategory: record.planCategory || null, // Use planCategory if available, otherwise null
          planDetails: [plan], // Wrap the plan in an array
        });
      });
    }
  });

  const chipsWithDisabled = Constants.BROWSE_AZ.slice(1).map(
    (letter: DisableableChipObject) => ({
      ...letter,
      isDisabled:
        letterMapping[letter.value]?.length === 0 && letter.value !== 'Clear',
    })
  );

  const form = useForm({
    defaultValues: {
      PLAN_SEARCH: '',
    },
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const categoriesToSort = selectedChip
    ? letterMapping[selectedChip] || [] // Use the categories for the selected chip
    : availablePlanNames;

  const sortCategories = (order: 'asc' | 'desc') =>
    [...categoriesToSort].sort((a, b) => {
      // Get the name for sorting
      const nameA =
        a?.planCategory ||
        (Array.isArray(a?.planDetails) && a.planDetails[0]?.planName) ||
        '';
      const nameB =
        b?.planCategory ||
        (Array.isArray(b?.planDetails) && b.planDetails[0]?.planName) ||
        '';

      // Compare names based on the sort order
      return order === 'asc'
        ? nameA.localeCompare(nameB) // A to Z
        : nameB.localeCompare(nameA); // Z to A
    });

  const sortOrderUpdated = useRef(false);
  const handleSortToggle = () => {
    sortOrderUpdated.current = true;
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc')); // Toggle sort order
  };

  const sortedCategories = sortCategories(sortOrder);

  const SEARCH_ERROR_DESCRIPTOR = t(
    'INPUT_MODELS.SPECIALITY_SEARCH_ERROR_DESCRIPTOR'
  );

  const onInputClear = () => {
    form?.clearErrors(Constants.INPUT_MODELS.PLAN_SEARCH as 'PLAN_SEARCH');
    setSearchText('');
  };

  const isGuestEni = Boolean(lob === 'EI');

  const onInputChange = (inputValue) => {
    form?.setValue(Constants.INPUT_MODELS.PLAN_SEARCH, inputValue, {
      shouldValidate: true,
      shouldDirty: true,
    });

    setSearchText(
      form?.getValues(Constants.INPUT_MODELS.PLAN_SEARCH as 'PLAN_SEARCH')
    );
  };
  const previous = useRef({ searchText }).current;

  useEffect(() => {
    if (
      previous.searchText.length === 0 &&
      searchText.length === 1 &&
      selectedChip !== null
    ) {
      setSelectedChip('');
    }
    return () => {
      previous.searchText = searchText;
    };
  }, [searchText]);

  const searchList = sortedCategories?.filter((item) => {
    const categoryNameMatch = item?.planCategory
      ?.toLocaleLowerCase()
      .includes(searchText.trim().toLocaleLowerCase());

    const planDetailsMatch = item?.planDetails?.some((plan) =>
      plan?.planName
        .toLocaleLowerCase()
        .includes(searchText?.trim()?.toLocaleLowerCase())
    );
    return categoryNameMatch || planDetailsMatch;
  });

  const allSearchedData = searchList?.filter((item) => {
    const planCategory = item?.planCategory || '';
    const planDetails = item?.planDetails || [];
    const search = searchText?.trim()?.toLocaleLowerCase();

    const categoryNameMatch = planCategory.toLocaleLowerCase().includes(search);

    const planDetailsMatch = planDetails.some((plan) =>
      plan?.planName.toLocaleLowerCase().includes(search)
    );

    return categoryNameMatch || planDetailsMatch;
  });

  const itemsWithCategoryName = allSearchedData?.filter(
    (item) => item.planCategory
  );

  const itemsWithoutCategoryName = allSearchedData
    ?.filter((item) => !item.planCategory) // Only items without a category name
    ?.map((item) => ({
      ...item,
      planDetails: item.planDetails || [],
    }));

  const selectedChipWithClear = (chip: string) => {
    if (chip === 'Clear') {
      const button = Array.from(document.querySelectorAll('button')).find(
        (btn) => btn.textContent.trim() === 'close'
      );

      if (button) {
        button.click();
      }
      setSelectedChip('');
      setSearchText('');
      form?.setValue(Constants.INPUT_MODELS.PLAN_SEARCH, '');
      form?.clearErrors(Constants.INPUT_MODELS.PLAN_SEARCH as 'PLAN_SEARCH');
    } else {
      setSelectedChip(chip);
    }
  };

  const coverageType = searchParams?.get('coverageType') || '';
  const chipValue = searchParams?.get('chipValue') || '';
  const fipscode = stateCode || sessionStorage.getItem('countyFipsCode') || '';
  const getFilters = () => {
    const filters = [searchText, selectedChip];
    if (sortOrderUpdated.current) {
      filters.push(sortOrder === 'asc' ? 'Plans A-Z' : 'Plans Z-A');
    }
    return filters.filter(Boolean).join('|');
  };
  const routeToPsxOrDualYearSelection = (
    selectedPlan: any,
    planIndex: number
  ) => {
    const isDualYearCheckEnabled = isDualYearCheckEnabledForGuest(
      lob,
      featureFlags,
      selectedPlan
    );
    adobeSearchResultClickEvent({
      filters: getFilters(),
      isGuest: true,
      linkName: selectedPlan.planName,
      linkPosition: planIndex + 1,
      term: selectedLocation.label || '',
      type: 'site',
    });

    if (isDualYearCheckEnabled) {
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA,
        JSON.stringify({
          selectedPlan,
          lob,
          coverageType: coverageType || 'M',
          stateCode: stateCode || '',
        })
      );
      navigate(
        `/plan-year-selection?planSelectionLob=${lob}&coverageType=${coverageType}&chipValue=${chipValue}`
      );
      return;
    }
    handleGuestPlanNavigation({
      getFpcGuestToken,
      selectedPlan,
      planYear: selectedPlan?.years?.[0].planYear,
      fipscode,
      FPC_BASE_URL: FPC_BASE_URL || '',
      zipcode,
      chipValue,
      coverageType,
      navigate,
      lob,
      stateCode,
      longitude,
      latitude,
      mapKey,
      signInUrl,
      toast,
      toastMessageForFpcTokenError,
    });
  };

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    guestLob: getLobForPortalData(lob),
    isGuest: true,
    pageName: getGuestResultsPageName(coverageType),
    sitesectionLevel1: 'guest plan selection',
    sitesectionLevel2: 'select coverage type',
  });
  const isInitialMount = useRef(true);

  useEffect(() => {
    adobePageTrackEvent();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      adobeSearchFilterEvent({
        isGuest: true,
        filters: getFilters(),
        term: selectedLocation.label || '',
        type: 'site',
      });
    }
  }, [searchText, selectedChip, sortOrder]);

  return (
    <PageBody>
      <ContainerForMultiplePlans>
        <SearchInputContainer>
          <PlanInputContainer>
            <Text css={planLabel}>{planName}</Text>
            <FormProvider state={form}>
              <StyledPlanSearch
                data-auto-testid="search-plan-input-box"
                data-testid="search-plan-input-box"
                height={24}
                hideLabel
                inputRightElement={
                  <IconSymbol color="#002677" icon="search" size={24} />
                }
                isClearable
                isLoading={false}
                keys={['value', 'plan']}
                label={planName}
                model={Constants.INPUT_MODELS.PLAN_SEARCH}
                onClear={onInputClear}
                onInputChange={onInputChange}
                openOnFocus={false}
                placeholder={enterPlanName || 'Enter a plan name'}
                rounded
                validators={{
                  validate: {
                    hasValue: (val) => {
                      let errorMessage;
                      if (
                        !Constants.INPUT_MODELS.SPECIALITY_SEARCH_REGEX_PATTERN.test(
                          val
                        )
                      ) {
                        errorMessage = SEARCH_ERROR_DESCRIPTOR;
                      }
                      if (!val) {
                        form.clearErrors(
                          Constants.INPUT_MODELS.PLAN_SEARCH as 'PLAN_SEARCH'
                        );
                        return null;
                      }
                      return errorMessage;
                    },
                  },
                }}
                value={searchText}
              />
            </FormProvider>
          </PlanInputContainer>
        </SearchInputContainer>

        <StyledContainerforPage>
          <BrowseAZ
            chips={chipsWithDisabled}
            guestUser={true}
            selectedChip={selectedChip}
            setSelectedChip={selectedChipWithClear}
          />

          <div style={{ borderTop: '1px solid #E5E5E6' }} />
          {availablePlanNames?.length === 0 ? (
            <Flex
              css={{ marginTop: '40px' }}
              data-testid="no-plans-in-selected-area"
              justify="center"
            >
              <Text color="$red" css={StyledTextForNoResults}>
                {noPlansInSelectedArea}
              </Text>
            </Flex>
          ) : (
            <>
              {allSearchedData?.length > 0 ? (
                <div style={{ padding: '24px 0' }}>
                  <Flex
                    css={{
                      gap: '24px',
                      maxWidth: '485px',
                    }}
                  >
                    {isGuestEni && (
                      <Flex css={{ gap: '4px' }} direction="column">
                        <Text css={StyledText}>{`${t(
                          'FILTER_LABELS.FILTER_BY'
                        )}:`}</Text>
                        <StyledFundedHealthButton>
                          {levelFundHealthPlans}
                        </StyledFundedHealthButton>
                      </Flex>
                    )}
                    <Flex css={{ gap: '4px' }} direction="column">
                      <SelectInputButton
                        data-testid="provider-search-type"
                        inputLeftElement={<IconSymbol icon="swap_vert" />}
                        label={`${t('FILTER_LABELS.SORT_BY')}:`}
                        onChange={handleSortToggle}
                        options={[
                          {
                            value: 'asc',
                            label: plansAZ,
                          },
                          {
                            value: 'desc',
                            label: plansZA,
                          },
                        ]}
                        placeholder={plansAZ ?? 'Plans A-Z'}
                        value={sortOrder}
                      />
                    </Flex>
                  </Flex>

                  <ContainerForPlanSelection>
                    {itemsWithCategoryName?.length > 0 && (
                      <>
                        {itemsWithCategoryName?.map(
                          (category: any, index: number) => (
                            <React.Fragment
                              key={category.planCategory.concat(index)}
                            >
                              {category.planCategory && (
                                <StyledCategoryContainer>
                                  <StyledListOfCategoryContainer>
                                    {category.planCategory}
                                  </StyledListOfCategoryContainer>
                                  <ContainerPlanNamesFlex>
                                    {category.planDetails.map(
                                      (plan: any, planIndex: number) => (
                                        <PlanNameLink
                                          data-testid={`guest-plan-names-${plan?.planName.replaceAll(
                                            ' ',
                                            '-'
                                          )}`}
                                          isStandardAnchor
                                          key={plan.planName.concat(planIndex)}
                                          onClick={() =>
                                            routeToPsxOrDualYearSelection(
                                              plan,
                                              planIndex
                                            )
                                          }
                                          openNewWindow={
                                            !isDualYearCheckEnabledForGuest(
                                              lob,
                                              featureFlags,
                                              plan
                                            ) && IsExternallink(plan)
                                          }
                                        >
                                          {plan.planName}
                                        </PlanNameLink>
                                      )
                                    )}
                                  </ContainerPlanNamesFlex>
                                </StyledCategoryContainer>
                              )}
                              <div style={{ borderTop: '1px solid #E5E5E6' }} />
                            </React.Fragment>
                          )
                        )}
                      </>
                    )}

                    {itemsWithoutCategoryName?.length > 0 && (
                      <ContainerPlanNamesFlex>
                        {itemsWithoutCategoryName.map(
                          (item: any, index: number) => (
                            <React.Fragment
                              key={
                                item.planDetails[0]?.planName.concat(index) ||
                                Math.random()
                              }
                            >
                              {item.planDetails.map(
                                (plan: any, planIndex: number) => (
                                  <PlanNameLink
                                    data-testid={`guest-plan-names-${plan?.planName
                                      .replaceAll(' ', '-')
                                      .toLowerCase()}`}
                                    isStandardAnchor
                                    key={plan.planName.concat(planIndex)}
                                    onClick={() =>
                                      routeToPsxOrDualYearSelection(
                                        plan,
                                        planIndex
                                      )
                                    }
                                    openNewWindow={
                                      !isDualYearCheckEnabledForGuest(
                                        lob,
                                        featureFlags,
                                        plan
                                      ) && IsExternallink(plan)
                                    }
                                  >
                                    {plan.planName}
                                  </PlanNameLink>
                                )
                              )}
                            </React.Fragment>
                          )
                        )}
                      </ContainerPlanNamesFlex>
                    )}
                  </ContainerForPlanSelection>
                </div>
              ) : (
                <Flex
                  css={{ marginTop: '40px' }}
                  data-testid="no-matching-plans"
                  justify="center"
                >
                  <Text css={StyledTextForNoResults}>{noMatchingPlans}</Text>
                </Flex>
              )}
            </>
          )}
        </StyledContainerforPage>
      </ContainerForMultiplePlans>
    </PageBody>
  );
};
